import React, { Fragment } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import { Box, Checkbox, Chip, CircularProgress, Grow, IconButton, InputAdornment, TablePagination, TextField } from '@mui/material';
import i18n from '../../i18n';
import ToolbarFiltterPopover, { FillterSelects } from './toolbar-popover';
import { Search } from '@mui/icons-material';
import ViewColumnPopover from './view-column-popover';
import { HeadCell } from './table.component';

const VirtuosoTableComponents: TableComponents<Data> & {} = {
    // eslint-disable-next-line react/display-name
    Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
        <TableContainer   {...props} ref={ref} />
    )),
    Table: (props) => (
        <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
    ),
    /** Virtuoso 对 TableHead 的签名与 MUI OverridableComponent 推断冲突，运行时直接使用 MUI TableHead */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TableHead: TableHead as any,
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    // eslint-disable-next-line react/display-name
    TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
        <TableBody {...props} ref={ref} />
    ))
};

function fixedHeaderContent(props: EnhancedTableProps) {
    const { onSelectAllClick, numSelected, rowCount, showChecked,
        selectType, columns, checkedColumnList } = props;
    return (
        <TableRow style={{ boxShadow: 'unset' }}>
            {/* 复选框 */}
            {
                showChecked !== 0 && (
                    <TableCell variant='head' padding='checkbox'
                        sx={{
                            backgroundColor: 'background.paper',
                        }} >
                        {selectType === 'RADIO' ? null :
                            <Checkbox
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={rowCount > 0 && numSelected === rowCount}
                                onChange={onSelectAllClick}
                                inputProps={{ 'aria-label': 'select all desserts' }}
                            />
                        }
                    </TableCell>
                )
            }
            {columns.map((column) => {
                if (checkedColumnList.includes(column.id)) {
                    return (
                        <TableCell
                            key={column.id}
                            variant='head'
                            // align={column.numeric || false ? 'right' : 'left'}
                            style={{ width: column.width }}
                            sx={{
                                backgroundColor: 'background.paper',
                            }}
                        >
                            {column.label}
                        </TableCell>
                    )
                } else {
                    return <Fragment key={column.id}></Fragment>
                }
            })}
        </TableRow>
    );
}


export default React.forwardRef(function ReactVirtualizedTable(props: ReactVirtualizedTableProps, ref) {

    React.useImperativeHandle(ref, () => {
        return {
            setSelected: (val: Data[]) => { setSelected(val) }
        }
    })

    const { data, columns, allCount, rowsPerPage, currentPage, changePageCallback, showChecked,
        compareSymbol = '_id', selectType, filterCheckData, handleCheck, customToolbarSelect, isLoading, fillterSelects,
        onFilterChange, onSearchChange, onSearch
    } = props;
    const [showSearch, setShowSearch] = React.useState(false);

    // 复选框选中的数据
    const [selected, setSelected] = React.useState<Data[]>([]);
    // 当前页数  切换页数
    const [page, setPage] = React.useState<number>(currentPage);
    // 设置当前页数显示的行数
    const [curRowsPerPage, setCurRowsPerPage] = React.useState<number>(rowsPerPage);
    const [fillterChips, setFillterChips] = React.useState<{ label: string, value: string, onDelete: () => void }[]>([]);
    // 动态列
    const [checkedColumnList, setCheckedList] = React.useState<string[]>([]);
    React.useEffect(() => {
        const newCheckedList = columns.map((item) => {
            return item.id
        })
        setCheckedList(newCheckedList)
    }, [])
    // 改变当前页数  下一页 | 上一页
    const handleSetPage = (event: any, newPage: number) => {
        setPage(newPage);
        changePageCallback?.({ page: newPage, curRowsPerPage });
        /**
         * 讨论决定 切换表格页数时重置复选框选择的数据
         */
        if (selected.length > 0) {
            setSelected([]);
        }
    }

    // 设置每页显示的...行数
    const handleSetRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rowsPerPage = parseInt(event.target.value, 10)
        setCurRowsPerPage(rowsPerPage);
        setPage(0);
        changePageCallback?.({ page: 0, curRowsPerPage: rowsPerPage });
        /**
         * 讨论决定 改变表格每页显示的行数时重置复选框选择的数据
         */
        if (selected.length > 0) {
            setSelected([]);
        }
    };
    function rowContent(_index: number, row: Data) {
        // 判断是否被选中了 当前行
        const isItemSelected = selected.findIndex((item) => {
            if (item[compareSymbol]) {
                return item[compareSymbol] === row[compareSymbol];
            }
            return false;
        }) !== -1;
        return (
            <React.Fragment>
                {/* 复选框 */}
                {
                    showChecked !== 0 && (
                        <TableCell
                            size='small'
                            padding='checkbox'
                            onClick={(event) => handleClick(event, row)}
                        >
                            <Checkbox
                                // disabled={checkDisabled(row)}
                                checked={isItemSelected}
                            // inputProps={{ 'aria-labelledby': labelId }}
                            />
                        </TableCell>
                    )
                }
                {columns.map((column) => {
                    if (checkedColumnList.includes(column.id)) {
                        return (
                            <TableCell
                                key={column.id}
                                width={column.width}
                            >
                                {column.renderFunction
                                    ? column.renderFunction(row, _index)
                                    : row[column.id]}
                            </TableCell>
                        )
                    } else {
                        return <Fragment key={column.id}></Fragment>
                    }
                })}
            </React.Fragment>
        );
    }
    // 单机选中单行
    const handleClick = (event: React.MouseEvent<unknown>, row: Data) => {
        /**
         * 1.这里单选状态和复选状态
         * 2.保存选中的数据
         * 3.过滤掉不需要的字段勾选的
         */
        // 不可以编辑 (不给高亮选中状态)
        if (showChecked === 0) return;

        // 选中的数据处理
        const selectedItem = selected.some((item) => {
            if (item[compareSymbol]) {
                return item[compareSymbol] === row[compareSymbol];
            }
            return false;
        });

        let newSelected: Data[] = [];
        if (selectType === 'RADIO') {
            if (!selectedItem) {
                if (filterCheckData?.([row])?.length === 0) return;
                newSelected = [row];
            } else {
                newSelected = [];
            }
        } else {
            if (!selectedItem) {
                newSelected = [...selected, row];
            } else {
                newSelected = selected.filter((item) => {
                    return !(item[compareSymbol] === row[compareSymbol]);
                });
            }
        }
        // $ 数据过滤
        // newSelected = filterCheckData?.(newSelected) || newSelected;
        const finalSelected = filterCheckData?.(newSelected.filter((item) => !!item)) || newSelected;
        handleCheck?.(finalSelected || []);
        setSelected(finalSelected || newSelected);
    };
    // 选中所有或者清除所有
    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        /**
         * 1.设置选中只选中当前页行数的数据
         * 2.全选按钮,选中取消,没有选中全选
         * 3.触发父类的 handleCheck方法
         * $ 4.过滤不需要被勾选在的数据
         */
        if (event.target.checked) {
            // 设置选中只选中当前页行数的数据
            const currentRowsSelected = data
            // $ 数据过滤
            const newSelected = filterCheckData?.(currentRowsSelected) || currentRowsSelected;
            handleCheck?.(newSelected);
            setSelected(newSelected);
            return;
        }
        handleCheck?.([]);
        setSelected([]);
    };
    return (
        <Paper elevation={3} className='react-virtualized-table'>
            {isLoading && <Box className='table-loading' sx={(theme) => { return { background: `${theme.palette.background.default}` } }}>
                <CircularProgress color='primary' />
            </Box>}
            {<Box display={'flex'} padding={'12px'}>
                <Box flexGrow={1} flexShrink={1} display={'flex'} alignItems={'center'}   >
                    <Grow
                        in={showSearch}
                    >
                        <TextField
                            className='searchInput'
                            id='standard-end-adornment'
                            size={'small'}
                            onChange={(event: { target: { value: string; }; }) => {
                                onSearchChange?.(event.target.value);
                            }}
                            // value={inputSearchValue}
                            onKeyDown={( ) => {
                                // this.props.onSearch(searchItemKey, inputSearchValue);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton
                                            onClick={() => { onSearch?.(); }}
                                        >
                                            <Search />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grow>
                </Box>
                {/* 自定义工具栏 */}
                {!!selected.length && customToolbarSelect?.()}
                {/* 默认工具栏 */}
                {!selected.length && <Box style={{ padding: '2px 12px' }}>
                    {/* 搜索器 */}
                    <IconButton onClick={() => { setShowSearch((s) => !s) }}>
                        <Search sx={(theme) => { return showSearch ? { color: `${theme.palette.primary.main}` } : {} }} />
                    </IconButton>
                    {/* 显示列 */}
                    <ViewColumnPopover columns={columns} checkedColumnList={checkedColumnList} changeCallback={(val) => { setCheckedList([...val]) }} />
                    {/* 过滤器 */}
                    <ToolbarFiltterPopover
                        selects={fillterSelects!}
                        changeCallback={(val, chips) => {
                            onFilterChange?.(val);
                            setFillterChips(chips);
                        }}
                    />
                </Box>}
            </Box>}
            <Box paddingLeft={'12px'} paddingRight={'12px'}>
                {/* 过滤器已选标签 */}
                {fillterChips?.map((item, index) => {
                    return <Chip
                        key={`${item.value}_${index}`}
                        style={{ marginRight: '8px', marginBottom: '8px' }}
                        label={item.label}
                        onDelete={() => { item.onDelete() }}
                    />
                })}
            </Box>
            <Paper style={{ height: '55vh', width: '100%', boxShadow: 'unset' }}>
                <TableVirtuoso
                    data={data}
                    components={{
                        ...VirtuosoTableComponents,
                        EmptyPlaceholder: () => (
                            isLoading ? <></> :
                                <Box className='table-loading'>
                                    {i18n.t('google_text_public_text_no_data')}
                                </Box>
                        )
                    }}
                    fixedHeaderContent={() => fixedHeaderContent({
                        columns,
                        numSelected: selected.length,
                        rowCount: selected.length,
                        showChecked,
                        onSelectAllClick (event: React.ChangeEvent<HTMLInputElement>): void {
                            handleSelectAllClick(event)
                        },
                        checkedColumnList
                    })}
                    itemContent={(_index: number, row: Data) => rowContent(_index, row)}
                />
            </Paper>
            <TablePagination
                rowsPerPageOptions={[20, 50, 100, 500]}
                labelRowsPerPage={i18n.t('general_row_each_page')}
                component='div'
                count={allCount}
                rowsPerPage={curRowsPerPage}
                page={page}
                onPageChange={(e, page) => handleSetPage(e, page)}
                onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSetRowsPerPage(event)}
            // 自定义的页脚分页组件 isUseCustomPagination变量控制是否启用
            // ActionsComponent={isUseCustomPagination ? TablePaginationActions : undefined}
            />
        </Paper>
    );
})
// table组件
interface Data {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

interface EnhancedTableProps {
    columns: Array<HeadCell>;
    numSelected: number;
    rowCount: number;
    showChecked: number;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    // 单选或者多选，默认多选
    selectType?: 'RADIO' | 'CHECK';
    checkedColumnList: string[]
}
type ReactVirtualizedTableProps = {
    data: Data[],
    columns: HeadCell[],
    allCount: number,
    rowsPerPage: number,
    currentPage: number,
    // $ 操作切换页码、每页数量后返回当前页码、每页数量
    changePageCallback?: (params: { page: number; curRowsPerPage: number }) => void;
    // 是否显示复选框 0 不显示 1显示  2 显示但是不显示删除提示框
    showChecked: number;
    // 单选或者多选，默认多选
    selectType?: 'RADIO' | 'CHECK';
    // 作比较唯一表示  id \  _id \ item_id (后端唯一属性值，key不一致，因此可用传递唯一标识key ， 默认为_id)
    compareSymbol?: string;
    // $ 过滤复选框选中的数据
    filterCheckData?: (selected: Data[]) => Data[];
    // 复选按钮触发事件
    handleCheck?: (selected: Data[]) => void;
    customToolbarSelect?: () => React.ReactNode;
    isLoading: boolean;
    // 过滤器
    fillterSelects?: FillterSelects[];
    // 选择过滤选项
    onFilterChange?: (val: { [key: string]: string[] }) => void;
    // 搜索 输入框change
    onSearchChange?: (val: string) => void;
    // 点击搜索
    onSearch?: () => void;
}

